// Library Imports
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Collapse } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';

import { LeftArrowIcon } from '@/assets/icons/LeftArrowIcon';
import { LinkIcon } from '@/assets/icons/LinkIcon';
import { PencilIcon } from '@/assets/icons/PencilIcon';
import { capitalizeFirstLetter, useKitStates } from '@/utils';
import { normalizeRole, ROLES } from '@/router/roles';
import { getProfileAPIFn } from '@/redux/features/dashboardProfile/dashboardProfileSlice';
import PaymentModal from '@/components/Profile/PaymentModal';

/*************************** MENU LIST ITEM DESKTOP *****************************/
const ListItem = ({ child, text, isOpen, setIsOpen, route, setIsShow }) => {
  const dispatch = useDispatch();
  const { userId } = useKitStates()
  const [name, setName] = useState('');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width: 768px)');

  const currentUser = useSelector(state => state?.currentUser?.currentUser);
  const role = normalizeRole(currentUser?.userRole) ?? ROLES.CREATOR;
  const planType = useSelector(state => state?.currentUser?.currentUser?.planType) ?? {}
  const location = useLocation();
  const isParent = child?.some(
    childRoute =>
      childRoute?.parentModule === route?.moduleName && location?.pathname === childRoute?.path
  );
  const navigate = useNavigate();
  const handleClick = () => {
    if (child) {
      setIsOpen(!isOpen);
    }
  };

  const getUserDetails = async () => {
    if (role !== ROLES.CREATOR || text !== 'Profile' || !userId) return;
    const userDetails = await dispatch(getProfileAPIFn({ userId }));
    setName(userDetails?.payload?.firstName);
  };

  useEffect(() => {
    getUserDetails();
  }, [text, role]);


  const handleNavigate = (path) => {
    if (isMobile) {
      setIsShow(false);
    }
    navigate(path);
  };

  const onSuccessPayment = () => {
    navigate('/dashboard/manage-subscription');
  };


  return (
    <React.Fragment>
      <div
        className='flex justify-start px-4 py-4 cursor-pointer hover:bg-neutral-light4'
        onClick={handleClick}
      >
        <div className={`horizontal-center gap-2`}>
          {text === 'Profile' ? (
            <p className={`text-base font-outfit hover:underline hover:decoration-[1px] hover:underline-offset-[8px] uppercase  ${isParent ? 'font-medium underline decoration-[1px] underline-offset-[8px]' : 'font-light'} `}>
              {`${capitalizeFirstLetter(name) || 'User'}'s`} {text}
            </p>
          ) : (
            <p className={`text-base font-outfit hover:underline hover:decoration-[1px] hover:underline-offset-[8px] uppercase  ${isParent ? 'font-medium underline decoration-[1px] underline-offset-[8px]' : 'font-light'} `}>
              {text}
            </p>
          )}
          <LeftArrowIcon
            className={`transition-transform duration-300 ${isOpen ? '-rotate-120' : 'rotate-180'}`}
          />
        </div>
      </div>
      <Collapse in={isOpen} timeout={300} unmountOnExit>
        <div className='flex flex-col'>
          {child
            ?.filter(route =>
              !route?.planType ||
              route?.planType === planType ||
              (planType === "cancelled" && route?.planType === "paid")
            )?.map((route, index) => {
              const isActive = location?.pathname === route?.path;

              return (
                <React.Fragment key={index}>
                  {route?.moduleName === 'shareable-link' ? (
                    <div
                      className={`pr-8 pl-4 py-2 cursor-pointer hover:bg-neutral-light4 ${isActive ? 'bg-neutral-light4' : ''}`}
                      key={index}
                      onClick={() => handleNavigate(route?.path)}
                    >
                      <p
                        className={`flex items-center gap-1 text-base font-outfit leading-5 hover:underline hover:decoration-[1px] w-fit hover:underline-offset-4 rounded bg-secondary-peach border border-[#C5B5A299] py-1.5 px-2 ${isActive ? 'font-medium underline decoration-[1px] underline-offset-4' : 'font-light'}`}>
                        <LinkIcon className='w-5 h-5' />
                        {route?.text}
                      </p>
                    </div>
                  ) : route?.moduleName === 'edit-kit' ? (
                    <div
                      className={`px-7 py-2 cursor-pointer hover:bg-neutral-light4 ${isActive ? 'bg-neutral-light4' : ''}`}
                      key={index}
                      onClick={() =>
                        navigate(`/media-kit/${userId}`, {
                          state: {
                            edit: true,
                            upgrade: true,
                            from: '/dashboard/profile-account'
                          }
                        })
                      }
                    >
                      <p
                        className={`flex items-center gap-2 text-base font-outfit leading-5 hover:underline hover:decoration-[1px] hover:underline-offset-4 ${isActive ? 'font-medium underline decoration-[1px] underline-offset-4' : 'font-light'}`}>
                        <PencilIcon
                          className={`cursor-pointer size-4 !text-white p-[3px] bg-secondary-lightMintGreen border border-black rounded`}
                        />
                        {route?.text}
                      </p>
                    </div>
                  ) : route?.moduleName === 'upgrade-media-kit' ? (
                    <div
                      className={`px-7 py-2 cursor-pointer hover:bg-neutral-light4 ${isActive ? 'bg-neutral-light4' : ''}`}
                      key={index}
                      onClick={() => setIsPaymentModalOpen(true)}
                    >
                      <p
                        className={`text-base font-outfit leading-5 hover:underline hover:decoration-[1px] hover:underline-offset-4 ${isActive ? 'font-medium underline decoration-[1px] underline-offset-4' : 'font-light'}`}>
                        {route?.text}
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`px-7 py-2 cursor-pointer hover:bg-neutral-light4 ${isActive ? 'bg-neutral-light4' : ''}`}
                      key={index}
                      onClick={() => handleNavigate(route?.path)}
                    >
                      <p
                        className={`text-base font-outfit leading-5 hover:underline hover:decoration-[1px] hover:underline-offset-4 ${isActive ? 'font-medium underline decoration-[1px] underline-offset-4' : 'font-light'}`}>
                        {route?.text}
                      </p>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
        </div>
      </Collapse>

      {role === ROLES.CREATOR && isPaymentModalOpen ? (
        <PaymentModal isOpen={isPaymentModalOpen} setIsOpen={setIsPaymentModalOpen} onSuccessPayment={onSuccessPayment} />
      ) : null}
    </React.Fragment>
  );
};


// LIST ICON DESKTOP
export const ListIcon = ({ route, text, setIsShow }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(
    route?.child?.some(childRoute => location?.pathname === childRoute?.path)
  );
  return (
    <React.Fragment>
      <ListItem
        child={route.child}
        text={text}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        route={route}
        setIsShow={setIsShow}
      />
    </React.Fragment>
  );
};
